import * as React from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useParams,
  type RouteObject,
} from 'react-router';
import { Landing, rememberProject } from '../modules/platform/index.ts';
import { ForgotPasswordPage } from '../modules/identity/index.ts';
import { InviteAcceptPage } from '../modules/identity/index.ts';
import { LoginPage } from '../modules/identity/index.ts';
import { ResetPasswordPage } from '../modules/identity/index.ts';
import { SignUpPage } from '../modules/identity/index.ts';
import { VerifyEmailPage } from '../modules/identity/index.ts';
import { MfaVerificationPage } from '../modules/identity/index.ts';
import { NotFound } from '../modules/platform/index.ts';
import { PlaceholderPage } from '../modules/platform/index.ts';
import { Layout } from '../shell/Layout.tsx';
import { RouteErrorBoundary } from '../shell/RouteErrorBoundary.tsx';
import { ReconnectingBanner, ReconnectingScreen } from '../shell/Reconnecting.tsx';
import { safeReturn } from '../modules/identity/index.ts';
import { isAppUserOnly, useAuth } from '../modules/identity/index.ts';
import { parseProjectPath } from '../modules/platform/index.ts';
import { getUiView } from '../modules/platform/index.ts';
import { BUILT_PAGES, PROJECT_ROUTES } from './routes.ts';

/**
 * Signed-out callers go to /login, remembering where they were headed. A platform that does not answer is
 * not a sign-out: the page waits, "Reconnecting…" (Board #2649).
 */
export function ProtectedRoute() {
  const { state } = useAuth();
  const location = useLocation();
  if (state.status === 'loading') return <div className="min-h-screen bg-page-bg" />;
  if (state.status === 'reconnecting') return <ReconnectingScreen />;
  if (state.status === 'signed-out') return <Navigate to="/login" replace state={{ from: location }} />;
  return (
    <>
      <ReconnectingBanner />
      <Outlet />
    </>
  );
}

/**
 * A signed-in caller who opens /login is sent back to where they came from, or on to the `?return=` this app
 * allows (safeReturn: this origin only).
 */
export function PublicRoute() {
  const { state } = useAuth();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const external = safeReturn(query.get('return') ?? query.get('return_to'));
  React.useEffect(() => {
    if (state.status === 'signed-in' && external) window.location.replace(external);
  }, [state.status, external]);
  if (state.status === 'signed-in') {
    if (external) return <div className="min-h-screen bg-page-bg" data-testid="login-continue" />;
    const from = (location.state as { from?: { pathname: string; search?: string } } | null)?.from;
    return <Navigate to={from ? `${from.pathname}${from.search ?? ''}` : '/'} replace />;
  }
  return (
    <>
      <ReconnectingBanner />
      <Outlet />
    </>
  );
}

/** Remembers the project in the URL so "/" can return to it. */
function ProjectScope() {
  const { slug, branch } = useParams();
  React.useEffect(() => {
    if (slug && branch) rememberProject(slug, branch);
  }, [slug, branch]);
  return <Outlet />;
}

/** /p/:slug without a branch → the same page on main. */
function AddDefaultBranch() {
  const { slug = '', '*': rest = '' } = useParams();
  return <Navigate to={`/p/${encodeURIComponent(slug)}/b/main${rest ? `/${rest}` : ''}`} replace />;
}

const MyApps = React.lazy(() =>
  import('../modules/mini-apps/index.ts')
    .then((door) => door.pages['apps/MyApps']())
    .then((m) => ({ default: m.MyAppsPage })),
);

/** "/": cortex's landing, or, for an app user, the apps shared with them (no cortex shell). */
function Home() {
  const { state } = useAuth();
  if (!isAppUserOnly(state)) return <Landing />;
  return (
    <React.Suspense fallback={<div className="min-h-screen bg-page-bg" />}>
      <MyApps />
    </React.Suspense>
  );
}

/**
 * Cortex itself (every project page, the chat report, Code City) is not for an APP USER: they reach only the apps
 * shared with them, by their share links (the owner, 2026-11-03). An app's page in the shell (`…/apps/<app>`) goes to
 * the same app's share link; anything else to "/" (their apps). The api refuses them as well.
 */
export function AppUserGate() {
  const { state } = useAuth();
  const { pathname } = useLocation();
  if (!isAppUserOnly(state)) return <Outlet />;
  const p = parseProjectPath(pathname);
  const app = /^apps\/([^/]+)$/.exec(p.rest ?? '')?.[1];
  if (p.slug && p.branch && app)
    return (
      <Navigate
        to={`/p/${encodeURIComponent(p.slug)}/b/${encodeURIComponent(p.branch)}/share/apps/${app}`}
        replace
      />
    );
  return <Navigate to="/" replace />;
}

const Dashboard = React.lazy(() => BUILT_PAGES.dashboard().then((Page) => ({ default: Page })));

/** The project root: Dashboard in Advanced mode, Apps in Standard mode. */
function ProjectHome() {
  return getUiView() === 'simple' ? (
    <Navigate to="apps" replace />
  ) : (
    <React.Suspense fallback={<div className="min-h-full bg-page-bg" />}>
      <Dashboard />
    </React.Suspense>
  );
}

// errorElement sits on each PAGE route so a crashing page keeps the shell (and
// its nav) alive; the top-level routes carry a full-screen one.
const pageError = <RouteErrorBoundary />;

export const projectChildren: RouteObject[] = [
  { index: true, element: <ProjectHome />, errorElement: pageError },
  ...PROJECT_ROUTES.map((r): RouteObject => {
    const built = BUILT_PAGES[r.path as keyof typeof BUILT_PAGES];
    return built
      ? {
          path: r.path,
          lazy: async () => ({ Component: await built() }),
          // Shown while the page's chunk loads on a first visit.
          hydrateFallbackElement: <div className="min-h-full bg-page-bg" />,
          errorElement: pageError,
        }
      : {
          path: r.path,
          element: <PlaceholderPage title={r.title} epic={r.epic} path={r.path} />,
          errorElement: pageError,
        };
  }),
  // Monitoring lives on each hosted instance in Deployments (ADR 0056): the page it once had sends old links there.
  { path: 'monitoring', element: <Navigate to="../deployments" replace />, errorElement: pageError },
  { path: '*', element: <NotFound />, errorElement: pageError },
];

export const routes: RouteObject[] = [
  {
    element: <PublicRoute />,
    errorElement: <RouteErrorBoundary fullScreen />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/mfa-verification', element: <MfaVerificationPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/signup', element: <SignUpPage /> },
    ],
  },
  // A confirmation link signs the person in from either state (ADR 0044 §3).
  {
    path: '/verify',
    element: <VerifyEmailPage />,
    errorElement: <RouteErrorBoundary fullScreen />,
  },
  // A reset link and an invitation link work signed in or out (ADR 0014 §10).
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
    errorElement: <RouteErrorBoundary fullScreen />,
  },
  { path: '/invite', element: <InviteAcceptPage />, errorElement: <RouteErrorBoundary fullScreen /> },
  // The share link of an app: its own sign-in gate (a signed-out visitor signs in on the page, never at the
  // main login), then the app full screen.
  {
    path: '/p/:slug/b/:branch/share/apps/:viewName',
    lazy: async () => ({
      Component: (
        await import('../modules/mini-apps/index.ts').then((door) => door.pages['apps/SharedApp']())
      ).SharedAppPage,
    }),
    hydrateFallbackElement: <div className="min-h-screen bg-page-bg" />,
    errorElement: <RouteErrorBoundary fullScreen />,
  },
  // The design-system page: every primitive in both themes (no sign-in needed).
  {
    path: '/ui-gallery',
    lazy: async () => ({
      Component: (await import('../modules/platform/index.ts').then((door) => door.pages['pages/Gallery']()))
        .GalleryPage,
    }),
    errorElement: <RouteErrorBoundary fullScreen />,
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary fullScreen />,
    children: [
      { index: true, element: <Home /> },
      {
        element: <AppUserGate />,
        children: [
          // A saved chat report on its own (the chat's "Open ↗" popup, the rail): no shell, as the old
          // report.html was a page of its own.
          {
            path: 'p/:slug/b/:branch/chat-report',
            lazy: async () => ({
              Component: (await import('../modules/ai/index.ts').then((door) => door.pages['chat']()))
                .ChatReportRoute,
            }),
            hydrateFallbackElement: <div className="min-h-screen bg-page-bg" />,
            errorElement: <RouteErrorBoundary fullScreen />,
          },
          // Code City full screen in a tab of its own (its page's "Open in new tab"): no shell, the same session.
          ...['code-city', 'code-city/p/:slug/b/:branch'].map((path) => ({
            path,
            lazy: async () => ({
              Component: (await import('../modules/fleet/index.ts').then((door) => door.pages['code-city']()))
                .CodeCityTab,
            }),
            hydrateFallbackElement: <div className="min-h-screen bg-page-bg" />,
            errorElement: <RouteErrorBoundary fullScreen />,
          })),
          {
            path: 'p/:slug/b/:branch',
            element: <ProjectScope />,
            children: [{ element: <Layout />, children: projectChildren }],
          },
          { path: 'p/:slug/*', element: <AddDefaultBranch /> },
          { path: '*', element: <Landing /> },
        ],
      },
    ],
  },
];

export const createRouter = () => createBrowserRouter(routes);
